import { addDays } from 'date-fns';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { ErrorStateFallback } from '@/components/ui/error-state';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { INDEED, POOL } from '@/types/channels';

import { getChannelDefaults } from './channel-defaults';
import { Header } from './header';
import { JobPostingFlow } from './job-posting-flow';

const poolOpeningDefaults = {
  audienceId: null,
  subject: null,
  body: null,
};

export function JobPosting() {
  const location = useLocation();
  const {
    handlePostJob,
    clearJobPostingStorage,
    selectedChannelSlugs,
    selectedOpeningIds,
    unsupportedOpenings,
    hookForm,
  } = useJobPostingContext();
  const { setValue, getValues } = hookForm;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void handlePostJob();
  };

  // Clear storage when user navigates away from /post-job route. This won't trigger on page refresh since the component remounts with the same location
  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes('/post-job')) {
        clearJobPostingStorage();
      }
    };
  }, [location, clearJobPostingStorage]);

  // Opening budget for Zip will be 149
  const getOpeningBudget = useCallback((channelSlug: string) => {
    const fixedOpeningBudget = getChannelDefaults(channelSlug).budget;
    if (!fixedOpeningBudget) return null;
    return fixedOpeningBudget;
  }, []);

  // Generate the default opening budgets for the channel
  const generateOpeningDefaults = useCallback(
    (channelSlug: string) => {
      return selectedOpeningIds.reduce((acc, openingId) => {
        if (unsupportedOpenings[channelSlug]?.includes(openingId)) {
          return acc;
        }
        return {
          ...acc,
          [openingId]:
            channelSlug === POOL
              ? poolOpeningDefaults
              : getOpeningBudget(channelSlug),
        };
      }, {});
    },
    [getOpeningBudget, selectedOpeningIds, unsupportedOpenings],
  );

  //  Channel budget for Zip will be 149 * number of openings
  const getChannelBudget = useCallback(
    (channelSlug: string) => {
      const fixedOpeningBudget = getChannelDefaults(channelSlug).budget;
      if (!fixedOpeningBudget) return null;
      return (
        fixedOpeningBudget *
        Object.keys(generateOpeningDefaults(channelSlug)).length
      );
    },
    [generateOpeningDefaults],
  );

  const getChannelEndDate = useCallback((channelSlug: string) => {
    const fixedDuration = getChannelDefaults(channelSlug).duration;
    if (!fixedDuration) return null;
    return addDays(new Date(), fixedDuration).toISOString();
  }, []);

  // Initialize the default form data for channels and pool based on the selected channels and openings.
  useEffect(() => {
    const existingChannels = getValues('channels');
    // Only initialize if we have selected channels and haven't initialized yet
    if (selectedChannelSlugs.length > 0 && !existingChannels) {
      setValue(
        'channels',
        selectedChannelSlugs.reduce((acc, channelSlug) => {
          if (channelSlug === POOL) return acc; // Pool is handled in the Pool step, so we don't need to add it here

          return {
            ...acc,
            [channelSlug]: {
              startsAt: new Date().toISOString(),
              budgetAllocation: 'evenly' as const,
              budgetType: 'lifetime' as const,
              openingBudgets: generateOpeningDefaults(channelSlug),
              endAt: getChannelEndDate(channelSlug),
              channelBudget: getChannelBudget(channelSlug),
              totalBudget: null,
              isExpanded: true,
              endAtType: channelSlug === INDEED ? 'fixed' : null, // Indeed is the only channel with end date type
              slug: channelSlug,
              isValidated: false,
            },
          };
        }, {}),
      );
    }

    const existingPool = getValues('pool');
    if (!existingPool && selectedChannelSlugs.includes(POOL)) {
      setValue('pool', {
        campaignType: 'email',
        fromEmail: 'noreply@wxp.fountain.com', // TODO: This should be updated to null/empty string once this is implemented - https://linear.app/fountain/issue/AXR-888/integrate-senders-api-and-surface-from-email-selection-for-pool
        startsAt: new Date().toISOString(),
        endAt: null,
        isNeverEnding: false,
        openingMessages: generateOpeningDefaults(POOL),
      });
    }
  }, [
    selectedChannelSlugs,
    setValue,
    getValues,
    generateOpeningDefaults,
    getChannelBudget,
    getChannelEndDate,
  ]);

  return (
    <ErrorStateFallback>
      <form onSubmit={handleSubmit}>
        <div className="flex h-screen flex-col gap-6">
          <Header />

          <JobPostingFlow />
        </div>
      </form>
    </ErrorStateFallback>
  );
}
